import config from "appConfig";
import { getLinkForCourseLesson } from "../../../../services/Utils";

import lesson0402 from "../lesson-02/lesson";

const linkLesson0402 = getLinkForCourseLesson("e0db4477-1995-4380-9c35-2338dcf5e504", lesson0402.id);

const theory = `<h3 class=sectionHead>Introduction</h3>
<p>
   As was mentioned in the first Lesson, almost all audio transducers use a membrane to radiate a sound wave in the air. <a href=#>Figure 1</a> shows a slow motion movie of the displacement of a
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mn>21</mn>
   </math>
   cm diameter woofer. One can see that the movement is very homogeneous: all the points of the membrane seem to vibrate at the same velocity. This result is generally sought after by manufacturers of loudspeakers or microphones. A diaphragm whose individual parts do not vibrate in phase is likely to create artefacts in the transducer’s frequency or directivity response.<br class=newline>By considering this hypothesis, the displacement of the diaphragm can be assimilated to that of a rigid body moving in one direction. Its movement is then calculated by assuming its behaviour as a mechanical system with 1 Degree Of Freedom (1-DOF): the damped harmonic oscillator [1, 2, 3].
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/04/03/theory/figure-1.png alt="Figure 1. Movement of a loudspeaker membrane: first two seconds: real speed, last part: slow motion." data-video-src="${config.ASSETS_URL}/04/03/theory/video-1.mp4" data-video-alt="Figure 1. Movement of a loudspeaker membrane: first two seconds: real speed, last part: slow motion."><figcaption class=caption>Figure 1. Movement of a loudspeaker membrane: first two seconds: real speed, last part: slow motion.</figcaption></figure>
<h3 class=sectionHead>The damped harmonic oscillator</h3>
<p>
   Let us assume that the movement of the moving parts of a loudspeaker behaves as a 1-DOF mechanical system (see <a href=# data-img-src=${config.ASSETS_URL}/04/03/theory/figure-2.svg data-img-alt="Figure 2. Moving parts of an electrodynamic loudspeaker">Figure 2</a>). The diaphragm and coil form a rigid body of mass
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>M</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
   </math>
   which is attached to the basket, which is expected to be rigid and remain motionless, by means of two suspensions usually denoted the surround and the spider. It is assumed that these two suspensions are elastic and both form a spring of equivalent stiffness
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>K</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
   </math>, which measures the resistance offered by the suspensions when a force is applied to it. Like any real system, the energy supplied to the moving parts of the loudspeaker will be partly dissipated. We assume here that this dissipation is mostly due to frictional forces, mainly located in the suspensions, we will model them by a viscous damping coefficient also called mechanical resistance
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
   </math>.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/04/03/theory/figure-2.svg alt="Figure 2. Moving parts of an electrodynamic loudspeaker."><figcaption class=caption>Figure 2. Moving parts of an electrodynamic loudspeaker.</figcaption></figure>
<p>
   When vibrating, the diaphragm is subjected to a restoring force
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>F</mi>
         </mrow>
         <mrow>
            <mi>r</mi>
         </mrow>
      </msub>
   </math>
   exerted by the suspension elements. The force
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>F</mi>
         </mrow>
         <mrow>
            <mi>r</mi>
         </mrow>
      </msub>
   </math>
   is proportional to the diaphragm displacement
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>x</mi>
   </math>
<table class=equation>
   <tr>
      <td>
         <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>F</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>K</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
               </mrow>
            </msub>
            <mi>x</mi>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>x</mi>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(1)
</table>
<p>
   Note that electroacousticians often use the compliance (or flexibility)
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>C</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
   </math>
   which is the inverse of the stiffness
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>C</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mn>1</mn>
      <mo class=MathClass-bin>∕</mo>
      <msub>
         <mrow>
            <mi>K</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
</msub></math>.
<br class=newline>For a harmonic displacement
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>X</mi>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
   </math>, the restoring force can be expressed as a function of the membrane’s velocity
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>V</mi>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
   </math>
<table class=equation>
   <tr>
      <td style="font-size: 14px;">
         <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>F</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>K</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
               </mrow>
            </msub>
            <mfrac>
               <mrow>
                  <mi>V</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi>ω</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>jω</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>V</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi>ω</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>jω</mi>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(2)
</table>
<p>
   Note that we use here one property of the harmonic signal studied in <a href=${linkLesson0402} target="_blank">Lesson 2</a>: the primitive integral can be replaced by a division by
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>jω</mi>
   </math>
   yielding to
<table class=equation>
   <tr>
      <td style="font-size: 10px;">
         <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <mi>x</mi>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mo>∫</mo>
            <mi>v</mi>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mi mathvariant=italic>dt</mi>
            <munderover>
               <mo class=MathClass-rel>→</mo>
               <mrow></mrow>
               <mrow>
                  <mi mathvariant=bold-script>ℱ</mi>
               </mrow>
            </munderover>
            <mi>X</mi>
            <mo class=MathClass-open>(</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>V</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi>ω</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>jω</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(3)
</table>
<p>
   In an analogous way to the restoring force, the inertial force
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>F</mi>
         </mrow>
         <mrow>
            <mi>i</mi>
         </mrow>
      </msub>
   </math>
   can be described as
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>F</mi>
         </mrow>
         <mrow>
            <mi>i</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>M</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
      <mi>a</mi>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>, where
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>a</mi>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   is the diaphragm acceleration. For a harmonic excitation,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>F</mi>
         </mrow>
         <mrow>
            <mi>i</mi>
         </mrow>
      </msub>
   </math>
   can be related to the membrane’s velocity using
<table class=equation>
   <tr>
      <td>
         <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>F</mi>
               </mrow>
               <mrow>
                  <mi>i</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mi mathvariant=italic>jω</mi>
            <msub>
               <mrow>
                  <mi>M</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
               </mrow>
            </msub>
            <mi>V</mi>
            <mo class=MathClass-open>(</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(4)
</table>
<p>
   using
   <span style="font-size: 14px;">
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>a</mi>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mi mathvariant=italic>dv</mi>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
         </mrow>
         <mrow>
            <mi mathvariant=italic>dt</mi>
         </mrow>
      </mfrac>
      <munderover>
         <mo class=MathClass-rel>→</mo>
         <mrow></mrow>
         <mrow>
            <mi mathvariant=bold-script>ℱ</mi>
         </mrow>
      </munderover>
      <mi>A</mi>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mi mathvariant=italic>jωV</mi>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
   </math></span>, as seen in <a href=${linkLesson0402} target="_blank">Lesson 2</a>.<br class=newline>The membrane motion is also slowed by friction mechanisms. This frictional force
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>F</mi>
         </mrow>
         <mrow>
            <mi>f</mi>
         </mrow>
      </msub>
   </math>
   is usually assumed to be proportional to the velocity of the membrane and to the mechanical resistance
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
   </math>
<table class=equation>
   <tr>
      <td>
         <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>F</mi>
               </mrow>
               <mrow>
                  <mi>f</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
               </mrow>
            </msub>
            <mi>V</mi>
            <mo class=MathClass-open>(</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(5)
</table>
<p>
   The three parameters
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>M</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
   </math>,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>C</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
   </math>, and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
   </math>
   are then sufficient to model the behaviour of the moving parts of a loudspeaker considered as a damped harmonic oscillator.
<h3 class=sectionHead>Frequency response</h3>
<p>
   Consider a damped harmonic oscillator experiencing a harmonic force
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>F</mi>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>F</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>jωt</mi>
         </mrow>
      </msup>
   </math>
   (see <a href=# data-img-src=${config.ASSETS_URL}/04/03/theory/figure-3.svg data-img-alt="Figure 3. Damped harmonic oscillator.">Figure 3</a>), the balance of forces is obtained by
<table class=equation>
   <tr>
      <td style="font-size: 14px;">
         <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <mi>F</mi>
            <mo class=MathClass-open>(</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-bin>−</mo>
            <msub>
               <mrow>
                  <mi>F</mi>
               </mrow>
               <mrow>
                  <mi>f</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-bin>−</mo>
            <msub>
               <mrow>
                  <mi>F</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-bin>−</mo>
            <msub>
               <mrow>
                  <mi>F</mi>
               </mrow>
               <mrow>
                  <mi>i</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mn>0</mn>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(6)
</table>
<figure class=figure>
   <p>
      <img src=${config.ASSETS_URL}/04/03/theory/figure-3.svg alt="Figure 3. Damped harmonic oscillator.">
      <figcaption class=caption>Figure 3. Damped harmonic oscillator.</figcaption>
</figure>
<p>
   Note that
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>F</mi>
         </mrow>
         <mrow>
            <mi>f</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
   </math>,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>F</mi>
         </mrow>
         <mrow>
            <mi>r</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>F</mi>
         </mrow>
         <mrow>
            <mi>i</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   are of negative sign because they oppose the excitation force
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>F</mi>
   </math>. By replacing
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>F</mi>
         </mrow>
         <mrow>
            <mi>f</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
   </math>,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>F</mi>
         </mrow>
         <mrow>
            <mi>r</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>F</mi>
         </mrow>
         <mrow>
            <mi>i</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   by their expressions as a function of
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>V</mi>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
   </math>, we get
<table class=equation>
   <tr>
      <td style="font-size: 10px;">
         <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <mi>F</mi>
            <mo class=MathClass-open>(</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mrow>
               <mo fence=true form=prefix>(</mo>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>+</mo>
                  <mi mathvariant=italic>jω</mi>
                  <msub>
                     <mrow>
                        <mi>M</mi>
                     </mrow>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>+</mo>
                  <mfrac>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>jω</mi>
                        <msub>
                           <mrow>
                              <mi>C</mi>
                           </mrow>
                           <mrow>
                              <mi>m</mi>
                           </mrow>
                        </msub>
                     </mrow>
                  </mfrac>
               </mrow>
               <mo fence=true form=postfix>)</mo>
            </mrow>
            <mi>V</mi>
            <mo class=MathClass-open>(</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(7)
</table>
<p>
   The mechanical impedance
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
   </math>
   of the damped harmonic oscillator is then obtained by
<table class=equation>
   <tr>
      <td style="font-size: 12px;">
         <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>F</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi>ω</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <mi>V</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi>ω</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <mi mathvariant=italic>jω</mi>
            <msub>
               <mrow>
                  <mi>M</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>jω</mi>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(8)
</table>
<p>
   As for the electrical impedance (see <a href=${linkLesson0402} target="_blank">Lesson 2</a>), the mechanical impedance represents the opposition of a system to an excitation. Note that the admittance
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Y</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mn>1</mn>
      <mo class=MathClass-bin>∕</mo>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
   </math>
   can also be used. The frequency response of
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>V</mi>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
   </math>
<table class=equation>
   <tr>
      <td style="font-size: 10px;">
         <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <mi>V</mi>
            <mo class=MathClass-open>(</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>F</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi>ω</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>F</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi>ω</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>+</mo>
                  <mi mathvariant=italic>jω</mi>
                  <msub>
                     <mrow>
                        <mi>M</mi>
                     </mrow>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>+</mo>
                  <mfrac>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>jω</mi>
                        <msub>
                           <mrow>
                              <mi>C</mi>
                           </mrow>
                           <mrow>
                              <mi>m</mi>
                           </mrow>
                        </msub>
                     </mrow>
                  </mfrac>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(9)
</table>
<p>
   is shown in <a href=# data-img-src=${config.ASSETS_URL}/04/03/theory/figure-4.svg data-img-alt="Figure 4. Velocity frequency response of a damped harmonic oscillator for different values of <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>R</mi></mrow><mrow><mi>m</mi></mrow></msub></math> with <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>M</mi></mrow><mrow><mi>m</mi></mrow></msub><mo class=MathClass-rel>=</mo><mn>1</mn></math> kg, <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>C</mi></mrow><mrow><mi>m</mi></mrow></msub><mo class=MathClass-rel>=</mo><mn>1</mn></math> m/N and <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>F</mi></mrow><mrow><mi>m</mi></mrow></msub><mo class=MathClass-rel>=</mo><mn>1</mn></math> N.">Figure 4</a> for different values of
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
   </math>. One can observe that
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mo class=MathClass-rel>|</mo>
      <mi>V</mi>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>|</mo>
   </math>
   is maximum at the natural frequency
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
   </math>, with
<table class=equation>
   <tr>
      <td style="font-size: 12px;">
         <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>f</mi>
               </mrow>
               <mrow>
                  <mi>s</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mn>2</mn>
                  <mi>π</mi>
               </mrow>
            </mfrac>
            <msqrt>
               <mrow>
                  <mfrac>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>K</mi>
                           </mrow>
                           <mrow>
                              <mi>m</mi>
                           </mrow>
                        </msub>
                     </mrow>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>M</mi>
                           </mrow>
                           <mrow>
                              <mi>m</mi>
                           </mrow>
                        </msub>
                     </mrow>
                  </mfrac>
               </mrow>
            </msqrt>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mn>2</mn>
                  <mi>π</mi>
               </mrow>
            </mfrac>
            <msqrt>
               <mrow>
                  <mfrac>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>C</mi>
                           </mrow>
                           <mrow>
                              <mi>m</mi>
                           </mrow>
                        </msub>
                        <msub>
                           <mrow>
                              <mi>M</mi>
                           </mrow>
                           <mrow>
                              <mi>m</mi>
                           </mrow>
                        </msub>
                     </mrow>
                  </mfrac>
               </mrow>
            </msqrt>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(10)
</table>
<p>
   which is obtained by minimizing the denominator of
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>V</mi>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   (see Eq. 9). To do so,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>ω</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mn>2</mn>
      <mi>π</mi>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
   </math>
   is computed by canceling the imaginary part of
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
   </math>:
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>j</mi>
      <msub>
         <mrow>
            <mi>ω</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
      <msub>
         <mrow>
            <mi>M</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
      <mo class=MathClass-bin>+</mo>
      <mn>1</mn>
      <mo class=MathClass-bin>∕</mo>
      <mo class=MathClass-open>(</mo>
      <mi>j</mi>
      <msub>
         <mrow>
            <mi>ω</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
      <msub>
         <mrow>
            <mi>C</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/04/03/theory/figure-4.svg alt="Figure 4. Velocity frequency response of a damped harmonic oscillator for different values of <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>R</mi></mrow><mrow><mi>m</mi></mrow></msub></math> with <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>M</mi></mrow><mrow><mi>m</mi></mrow></msub><mo class=MathClass-rel>=</mo><mn>1</mn></math> kg, <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>C</mi></mrow><mrow><mi>m</mi></mrow></msub><mo class=MathClass-rel>=</mo><mn>1</mn></math> m/N and <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>F</mi></mrow><mrow><mi>m</mi></mrow></msub><mo class=MathClass-rel>=</mo><mn>1</mn></math> N."><figcaption class=caption>Figure 4. Velocity frequency response of a damped harmonic oscillator for different values of <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>R</mi></mrow><mrow><mi>m</mi></mrow></msub></math> with <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>M</mi></mrow><mrow><mi>m</mi></mrow></msub><mo class=MathClass-rel>=</mo><mn>1</mn></math> kg, <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>C</mi></mrow><mrow><mi>m</mi></mrow></msub><mo class=MathClass-rel>=</mo><mn>1</mn></math> m/N and <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>F</mi></mrow><mrow><mi>m</mi></mrow></msub><mo class=MathClass-rel>=</mo><mn>1</mn></math> N.</figcaption></figure>
<p>
   At lower frequencies, for
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>f</mi>
      <mo class=MathClass-rel>≪</mo>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
   </math>, the phase of
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>V</mi>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   (see Eq. 9) tends to
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>π</mi>
      <mo class=MathClass-bin>∕</mo>
      <mn>2</mn>
   </math>: we then have
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>V</mi>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>≃</mo>
      <mfrac>
         <mrow>
            <mi mathvariant=italic>jωF</mi>
            <mo class=MathClass-open>(</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
         </mrow>
         <mrow>
            <mi mathvariant=italic>Cm</mi>
         </mrow>
      </mfrac>
   </math>, which means that the behaviour of the oscillator is governed by its stiffness while at higher frequencies, for
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>f</mi>
      <mo class=MathClass-rel>≫</mo>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
   </math>, a phase of
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mo class=MathClass-bin>−</mo>
      <mi>π</mi>
      <mo class=MathClass-bin>∕</mo>
      <mn>2</mn>
   </math>
   indicates that mass has a predominant influence:
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>V</mi>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>≃</mo>
      <mfrac>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mi mathvariant=italic>jF</mi>
            <mo class=MathClass-open>(</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ωMm</mi>
         </mrow>
      </mfrac>
   </math>).<br class=newline>At the natural frequency (<em><span class=cmti-10>i.e.</span></em> for
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>f</mi>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
   </math>),
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>F</mi>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
      <mi>V</mi>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
   </math>, the system is only governed by its resistance, the effect of the restoring force due of the suspension cancel out the inertial force cancel due to the mass of the membrane. The force
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>F</mi>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   and the velocity
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>V</mi>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   are then in phase.
<p>
   One can see that, when
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
   </math>
   increases,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mo class=MathClass-rel>|</mo>
      <mi>V</mi>
      <mo class=MathClass-rel>|</mo>
   </math>
   is getting smaller and the bandwidth is increasing. This is a typical result for electrodynamic microphone: it is difficult to achieve both high bandwidth and high efficiency at the same time.<br class=newline>The quality factor
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>Q</mi>
   </math>
   is often used to describe the damping of an oscillator. It is defined as the ratio between the stored energy and the dissipated energy per cycle. This dimensionless parameter is given by
<table class=equation>
   <tr>
      <td>
         <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <mi>Q</mi>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <msqrt>
               <mrow>
                  <mfrac>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>M</mi>
                           </mrow>
                           <mrow>
                              <mi>m</mi>
                           </mrow>
                        </msub>
                     </mrow>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>C</mi>
                           </mrow>
                           <mrow>
                              <mi>m</mi>
                           </mrow>
                        </msub>
                     </mrow>
                  </mfrac>
               </mrow>
            </msqrt>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>f</mi>
                     </mrow>
                     <mrow>
                        <mi>s</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>Δf</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(11)
</table>
<p>
   where
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>Δf</mi>
   </math>
   is the bandwidth for which the energy of the system is at least half its peak value. In decibel (dB) scale, the bandwidth is bounded by the levels
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mn>3</mn>
   </math>
    dB below the maximum level. Note that the larger the
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>Q</mi>
   </math>
   value, the sharper is the peak of the curve. Systems with a quality factor above
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mn>0</mn>
      <mo class=MathClass-punc>.</mo>
      <mn>5</mn>
   </math>
   are referred to as under-damped, while those with a quality factor below
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mn>0</mn>
      <mo class=MathClass-punc>.</mo>
      <mn>5</mn>
   </math>
   are referred to as over-damped. The value of
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>Q</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
      <mo class=MathClass-punc>.</mo>
      <mn>5</mn>
   </math>
   corresponds to critically damped systems, <em><span class=cmti-10>i.e.</span></em> the maximum value of
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>Q</mi>
   </math>
   for which (and below which) the motion of the system decays without oscillation when excited by a pulse.<br class=newline>
<p>
   Frequency responses are shown in <a href=# data-img-src=${config.ASSETS_URL}/04/03/theory/figure-5.svg data-img-alt="Figure 5. Displacement frequency response of a damped harmonic oscillator for different values of <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>R</mi></mrow><mrow><mi>m</mi></mrow></msub></math> with <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>M</mi></mrow><mrow><mi>m</mi></mrow></msub><mo class=MathClass-rel>=</mo><mn>1</mn></math> kg, <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>C</mi></mrow><mrow><mi>m</mi></mrow></msub><mo class=MathClass-rel>=</mo><mn>1</mn></math> m/N and <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>F</mi></mrow><mrow><mi>m</mi></mrow></msub><mo class=MathClass-rel>=</mo><mn>1</mn></math> N.">Figure 5</a> for the displacement
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>X</mi>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mi>V</mi>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-bin>∕</mo>
      <mi mathvariant=italic>jω</mi>
   </math>
   and in <a href=# data-img-src=${config.ASSETS_URL}/04/03/theory/figure-6.svg data-img-alt="Figure 6. Acceleration frequency response of a damped harmonic oscillator for different values of <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>R</mi></mrow><mrow><mi>m</mi></mrow></msub></math> with <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>M</mi></mrow><mrow><mi>m</mi></mrow></msub><mo class=MathClass-rel>=</mo><mn>1</mn></math> kg, <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>C</mi></mrow><mrow><mi>m</mi></mrow></msub><mo class=MathClass-rel>=</mo><mn>1</mn></math> m/N and <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>F</mi></mrow><mrow><mi>m</mi></mrow></msub><mo class=MathClass-rel>=</mo><mn>1</mn></math> N.">Figure 6</a> for the acceleration
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>A</mi>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mi mathvariant=italic>jωV</mi>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
   </math>. Note that the displacement curve shows the behavior of a low-pass filter: the displacement is maximal and equal
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>C</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
      <msub>
         <mrow>
            <mi>F</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
   </math>
   for
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>f</mi>
      <mo class=MathClass-rel>→</mo>
      <mn>0</mn>
   </math>. At low frequencies, the movement is governed by the compliance: the more elastic the higher the displacement. At
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>f</mi>
      <mo class=MathClass-rel>≫</mo>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
   </math>, the displacement is decreasing with a rate of
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mn>12</mn>
   </math>
    dB per octave. Also, for high damping values, the transition around the resonant frequency is smoothed. For low damping, the transition bandwidth become narrower. For very low damping, a displacement peak occurs at the resonant frequency. The displacement phase is null at low frequencies while it is equal to
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mo class=MathClass-bin>−</mo>
      <mi>π</mi>
   </math>
   at high frequencies.<br class=newline>
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/04/03/theory/figure-5.svg alt="Figure 5. Displacement frequency response of a damped harmonic oscillator for different values of <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>R</mi></mrow><mrow><mi>m</mi></mrow></msub></math> with <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>M</mi></mrow><mrow><mi>m</mi></mrow></msub><mo class=MathClass-rel>=</mo><mn>1</mn></math> kg, <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>C</mi></mrow><mrow><mi>m</mi></mrow></msub><mo class=MathClass-rel>=</mo><mn>1</mn></math> m/N and <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>F</mi></mrow><mrow><mi>m</mi></mrow></msub><mo class=MathClass-rel>=</mo><mn>1</mn></math> N."><figcaption class=caption>Figure 5. Displacement frequency response of a damped harmonic oscillator for different values of <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>R</mi></mrow><mrow><mi>m</mi></mrow></msub></math> with <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>M</mi></mrow><mrow><mi>m</mi></mrow></msub><mo class=MathClass-rel>=</mo><mn>1</mn></math> kg, <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>C</mi></mrow><mrow><mi>m</mi></mrow></msub><mo class=MathClass-rel>=</mo><mn>1</mn></math> m/N and <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>F</mi></mrow><mrow><mi>m</mi></mrow></msub><mo class=MathClass-rel>=</mo><mn>1</mn></math> N.</figcaption></figure>
<p>
   Conversely, the acceleration curve has a high-pass filter shape. It reaches a maximum value equal to
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>F</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
      <mo class=MathClass-bin>∕</mo>
      <msub>
         <mrow>
            <mi>M</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
   </math>
   when
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>f</mi>
      <mo class=MathClass-rel>→</mo>
      <mi>∞</mi>
   </math>, showing that at high frequency the system behaviour is governed by the mass: the higher the mass, the lower the acceleration. At
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>f</mi>
      <mo class=MathClass-rel>≪</mo>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
   </math>, the acceleration is increasing with a rate of
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mn>12</mn>
   </math>
    dB per octave. The transition between region and the plateau is smoother and wider for higher damping, whereas it can become peaky for very low damping. The acceleration phase is equal to
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mo class=MathClass-bin>−</mo>
      <mi>π</mi>
   </math>
   at low frequencies while it is null at high frequencies.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/04/03/theory/figure-6.svg alt="Figure 6. Acceleration frequency response of a damped harmonic oscillator for different values of <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>R</mi></mrow><mrow><mi>m</mi></mrow></msub></math> with <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>M</mi></mrow><mrow><mi>m</mi></mrow></msub><mo class=MathClass-rel>=</mo><mn>1</mn></math> kg, <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>C</mi></mrow><mrow><mi>m</mi></mrow></msub><mo class=MathClass-rel>=</mo><mn>1</mn></math> m/N and <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>F</mi></mrow><mrow><mi>m</mi></mrow></msub><mo class=MathClass-rel>=</mo><mn>1</mn></math> N."><figcaption class=caption>Figure 6. Acceleration frequency response of a damped harmonic oscillator for different values of <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>R</mi></mrow><mrow><mi>m</mi></mrow></msub></math> with <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>M</mi></mrow><mrow><mi>m</mi></mrow></msub><mo class=MathClass-rel>=</mo><mn>1</mn></math> kg, <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>C</mi></mrow><mrow><mi>m</mi></mrow></msub><mo class=MathClass-rel>=</mo><mn>1</mn></math> m/N and <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>F</mi></mrow><mrow><mi>m</mi></mrow></msub><mo class=MathClass-rel>=</mo><mn>1</mn></math> N.</figcaption></figure>
<h3 class=sectionHead>N-DOF systems</h3>
<p>
   For some transducers, the mechanical behaviour can be more complex involving a larger number of resonances. In many cases, multi Degrees Of Freedom (N-DOF) systems can be used to model it. Each 1-DOF corresponds to an independent coordinate required to describe the system motion. <a href=# data-img-src=${config.ASSETS_URL}/04/03/theory/figure-7.svg data-img-alt="Figure 7. 2-DOF system">Figure 7</a> represents a 2 Degrees Of Freedom (2-DOF) system for which the two masses
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>M</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>M</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
   </math>
   can vibrate with two different velocities.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/04/03/theory/figure-7.svg alt="Figure 7. 2-DOF system."><figcaption class=caption>Figure 7. 2-DOF system.</figcaption></figure>
<h3 class=sectionHead>Application to audio transducers</h3>
<p>Because the moving parts of transducers mostly behave as a mass-spring system from a mechanical point of view, transducer manufacturers are tuning the resonance frequency of their systems depending on the targeted frequency range and on the transduction type. They can choose among a large variety of materials: paper, plastic, metal, carbon or synthetic fibers, etc. and play with the damping factor to optimize the system.<br class=newline>For loudspeakers, it is also possible to work on the shape of the membrane, <em><span class=cmti-10>e.g.</span></em> give it a cone shape or add ribs to stiffen the membrane. The shape of the diaphragm also plays an important role on the acoustic radiation at high-frequency.<br class=newline>Note that the parameters governing the behaviour of diaphragms and suspensions are generally frequency dependent. It is then possible to use parameters having complex and frequency dependent values in order to better predict the amplitudes and phases of the calculated quantities. This will be discussed in the Lessons dedicated to advanced loudspeaker modelling.
<h3 class=likesectionHead>Glossary</h3>
<p>
<div class=supertabular>
  <dl>
    <dt>1-DOF</dt>
    <dd>1 Degree Of Freedom.</dd>
    <dt>2-DOF</dt>
    <dd>2 Degrees Of Freedom.</dd>
    <dt>N-DOF</dt>
    <dd>multi Degrees Of Freedom.</dd>
  </dl>
</div>
<h3 class=sectionHead>References</h3>
<dl class=thebibliography>
   <dt id=X0-acoucou class=thebibliography>[1]
   <dd id=bib-1 class=thebibliography>
      <p><a href=https://ace.acoucou.org/ class=url><span class=cmtt-10>https://ace.acoucou.org/</span></a>. See for instance chapters 1 (background of mechanics) and 4 (Harmonic motion).
   <dt id=X0-eliseev2020theory class=thebibliography>[2]
   <dd id=bib-2 class=thebibliography>
      <p>Sergey Viktorovich Eliseev and Andrey Vladimirovich Eliseev. <span class=cmti-10>Theory of </span><span class=cmti-10>oscillations</span>. Springer, 2020. <span class=cmcsc-10><span class=small-caps>isbn</span></span>: 9783030312947.
   <dt id=X0-fahy2015fundamentals class=thebibliography>[3]
   <dd id=bib-3 class=thebibliography>
      <p>Frank Fahy and David Thompson. <span class=cmti-10>Fundamentals of sound and vibration</span>. CRC press, 2015. <span class=cmcsc-10><span class=small-caps>isbn</span></span>: 9780415562102.
</dl>`;

export default theory;
